import React, { useEffect } from 'react';
import { makeStyles } from '@material-ui/styles';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import {
  applicationsFetchedSelector,
  applicationsLoadingSelector,
  applicationsSelector,
  fetchCompanyApplications
} from '../../redux/features/applications/applicationsSlice';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(4)
  },
  content: {
    padding: 0
  },
  inner: {
    minWidth: 1050
  },
  nameContainer: {
    display: 'flex',
    alignItems: 'center'
  },
  avatar: {
    marginRight: theme.spacing(2)
  },
  actions: {
    justifyContent: 'flex-end'
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(6)
  },
  emptyState: {
    padding: theme.spacing(6),
    textAlign: 'center'
  },
  statusChip: {
    fontWeight: 600
  }
}));

const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'pending':
      return 'default';
    case 'reviewed':
      return 'primary';
    case 'accepted':
      return 'success';
    case 'rejected':
      return 'error';
    default:
      return 'default';
  }
};

const ApplicationsList = () => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const applications = useSelector(applicationsSelector);
  const isLoading = useSelector(applicationsLoadingSelector);
  const fetched = useSelector(applicationsFetchedSelector);

  useEffect(() => {
    // Only fetch if we haven't fetched yet
    if (!fetched && !isLoading) {
      console.log(
        '📋 [Applications] Fetching company applications for the first time'
      );
      dispatch(fetchCompanyApplications());
    }
  }, [dispatch, fetched, isLoading]);

  if (isLoading) {
    return (
      <div className={classes.root}>
        <Card>
          <CardHeader title="Job Applications" />
          <CardContent>
            <Box className={classes.loadingContainer}>
              <CircularProgress />
            </Box>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <Card>
        <CardHeader
          title="Job Applications"
          subheader={`View and manage applications from candidates`}
        />
        <CardContent className={classes.content}>
          {applications.length === 0 ? (
            <Box className={classes.emptyState}>
              <Typography variant="h5" gutterBottom>
                No applications yet
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Applications from candidates will appear here once they start
                applying to your jobs.
              </Typography>
            </Box>
          ) : (
            <div className={classes.inner}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Candidate Name</TableCell>
                    <TableCell>Job Title</TableCell>
                    <TableCell>Applied Date</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {applications.map((application) => (
                    <TableRow hover key={application.id}>
                      <TableCell>
                        <div className={classes.nameContainer}>
                          <Typography variant="body1">
                            {application.candidateName || 'Unknown Candidate'}
                          </Typography>
                        </div>
                      </TableCell>
                      <TableCell>{application.jobTitle}</TableCell>
                      <TableCell>
                        {new Date(
                          application.applicationDate
                        ).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <Chip
                          className={classes.statusChip}
                          label={application.status}
                          color={getStatusColor(application.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        {/* TODO: Add action buttons for status updates */}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ApplicationsList;
